import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import textsFetcher, { type TextsData } from "../services/api/TextsFetch";

type TextsContextValue = {
  texts: TextsData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

const defaultValue: TextsContextValue = {
  texts: null,
  loading: true,
  error: null,
  refetch: () => Promise.resolve(),
};

const TextsContext = createContext<TextsContextValue>(defaultValue);

export const useTextsContext = () => useContext(TextsContext);

export const TextsProvider = ({ children }: { children: ReactNode }) => {
  const [texts, setTexts] = useState<TextsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await textsFetcher.getTexts();
      setTexts(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error carregant textos");
      setTexts(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const value: TextsContextValue = {
    texts,
    loading,
    error,
    refetch: load,
  };

  return (
    <TextsContext.Provider value={value}>{children}</TextsContext.Provider>
  );
};
